import { useEffect } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "./store/auth";
import { api } from "./api/client";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Welcome from "./pages/Welcome";
import Dashboard from "./pages/Dashboard";
import InstagramAccounts from "./pages/InstagramAccounts";
import AddInstagramAccount from "./pages/AddInstagramAccount";
import Campaigns from "./pages/Campaigns";
import Discover from "./pages/Discover";
import DMTemplates from "./pages/DMTemplates";
import ActivityLog from "./pages/ActivityLog";
import Settings from "./pages/Settings";
import Admin from "./pages/Admin";
import Layout from "./components/Layout";

const ACTIVE_STATUSES = new Set(["active", "trialing", "past_due"]);

function hasActiveSubscription(status: string | null | undefined): boolean {
  return ACTIVE_STATUSES.has(status ?? "");
}

/** Just checks for a valid token — no subscription requirement (e.g. /settings, /welcome). */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useAuth((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

/**
 * Requires both a valid token AND an active subscription.
 * If the subscription is not active, redirects to /settings where the user
 * can pick or reactivate a plan.
 */
function SubscriptionRoute({ children }: { children: React.ReactNode }) {
  const token = useAuth((s) => s.token);
  const user = useAuth((s) => s.user);
  if (!token) return <Navigate to="/login" replace />;
  // subscriptionStatus === undefined means UserHydrator hasn't returned yet;
  // don't flash a redirect — wait until we know.
  const status = user?.subscriptionStatus;
  if (status !== undefined && !hasActiveSubscription(status)) {
    return <Navigate to="/settings" replace />;
  }
  return <Layout>{children}</Layout>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const token = useAuth((s) => s.token);
  const user = useAuth((s) => s.user);
  if (!token) return <Navigate to="/login" replace />;
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const token = useAuth((s) => s.token);
  if (token) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function HomeRoute() {
  const token = useAuth((s) => s.token);
  if (token) return <Navigate to="/dashboard" replace />;
  return <Landing />;
}

function UserHydrator() {
  const token = useAuth((s) => s.token);
  const setUser = useAuth((s) => s.setUser);
  useEffect(() => {
    if (!token) return;
    api.get("/api/auth/me").then(({ data }) => {
      setUser({
        id: data.id,
        email: data.email,
        name: data.name,
        plan: data.plan,
        subscriptionStatus: data.subscriptionStatus,
        role: data.role,
      });
    }).catch(() => {
      useAuth.getState().clear();
    });
  // Run only once on mount (token changes mean a fresh login, which already calls setSession)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

export default function App() {
  return (
    <>
    <UserHydrator />
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <Login />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicOnlyRoute>
            <Signup />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <Welcome />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <SubscriptionRoute>
            <Dashboard />
          </SubscriptionRoute>
        }
      />
      <Route
        path="/instagram-accounts"
        element={
          <SubscriptionRoute>
            <InstagramAccounts />
          </SubscriptionRoute>
        }
      />
      <Route
        path="/instagram-accounts/new"
        element={
          <SubscriptionRoute>
            <AddInstagramAccount />
          </SubscriptionRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <SubscriptionRoute>
            <Campaigns />
          </SubscriptionRoute>
        }
      />
      <Route
        path="/discover"
        element={
          <SubscriptionRoute>
            <Discover />
          </SubscriptionRoute>
        }
      />
      <Route
        path="/dm-templates"
        element={
          <SubscriptionRoute>
            <DMTemplates />
          </SubscriptionRoute>
        }
      />
      <Route
        path="/activity"
        element={
          <SubscriptionRoute>
            <ActivityLog />
          </SubscriptionRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <Admin />
          </AdminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
